import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { usePermissionValidator } from "../../../api/UserPermissionValidators";
import { CompanyUpdateInput } from "../../../api/Companies";
import {
  useCompanyUpdate,
  useCreateBehaviorKit,
  useGetCompanyEx,
} from "../../../api/CompaniesApi";

import { useBlocker } from "../../../utils/blocker";
import {
  ConfirmDialogActions,
  useConfirmationDialog,
} from "../../../shared-components/ConfirmationDialog";
import { Transition } from "history";

import LoadingSpinner from "../../../shared-components/LoadingSpinner";
import DropdownComboSimple, {
  DropdownItem,
} from "../../../shared-components/DropdownComboSimple";
import { BehaviorKitMinimalInfo } from "../../../api/Blueprints";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../../../shared-components/Button";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";
import CompanyBehaviorKitRemove from "../../companyEdit/CompanyBehaviorKitRemove";
import CompanyBehaviorKitDetail from "../../companyEdit/CompanyBehaviorKitDetail";

const defaultLocals: CompanyUpdateInput = {
  companyUUID: "",
  updates: new Map(),
};
const properties: string[] = ["default_behavior_kit"];
const bkItemsEmpty: DropdownItem[] = [{ id: "None", label: "General" }];

const BehaviorKits = () => {
  const { companyId, subid } = useParams();
  const activeId: string = !!subid ? subid : companyId ?? "";
  const permissions = usePermissionValidator();
  const [readOnly, setReadOnly] = useState<boolean>(true);
  const [readOnlyBehaviorKit, setReadOnlyBehaviorKit] = useState<boolean>(true);
  const [locals, setLocals] = useState<CompanyUpdateInput>(defaultLocals);
  const [dirty, setDirty] = useState<boolean>(false);
  const [transition, setTransition] = useState<Transition | undefined>(
    undefined
  );
  const [bkItems, setBkItems] = useState<DropdownItem[]>(bkItemsEmpty);
  const [bkList, setBkList] = useState<BehaviorKitMinimalInfo[]>([]);

  const {
    isLoading: loadingCompanyEx,
    data: companyEx,
    mutate: loadCompanyEx,
  } = useGetCompanyEx();
  const {
    isLoading: loadingCompanyUpdate,
    mutateAsync: updateCompanyAsync,
    error: companyUpdateError,
  } = useCompanyUpdate();
  const bkCreate = useCreateBehaviorKit();
  const navigate = useNavigate();

  if (
    (activeId === companyId && !permissions.companies.canViewBehaviorKits) ||
    (activeId === subid && !permissions.subcompanies.canViewBehaviorKits)
  ) {
    navigate("/unauthorized", { replace: true });
  }

  useEffect(() => {
    if (locals.companyUUID !== defaultLocals.companyUUID) {
      return;
    }
    loadCompanyEx(activeId);
    let newLocals = { ...locals };
    newLocals.companyUUID = activeId;
    setLocals(newLocals);
  }, [activeId, loadCompanyEx, setLocals, locals]);

  useEffect(() => {
    setReadOnly(
      activeId === companyId
        ? !permissions.companies.canEditCompanySetting
        : !permissions.subcompanies.canEditSubcompanySetting
    );
    setReadOnlyBehaviorKit(
      activeId === companyId
        ? !permissions.companies.canEditBehaviorKits
        : !permissions.subcompanies.canEditBehaviorKits
    );
  }, [
    permissions,
    setReadOnly,
    setReadOnlyBehaviorKit,
    activeId,
    companyId,
    subid,
  ]);

  useEffect(() => {
    if (!companyEx) {
      setLocals(defaultLocals);
      setBkItems(bkItemsEmpty);
      return;
    }
    const values = new Map<string, any>();
    let newBkItems: DropdownItem[] = [];
    let newBkList: BehaviorKitMinimalInfo[] = [];
    Object.keys(companyEx.behavior_kits).forEach((bk) => {
      newBkItems.push({ id: bk, label: companyEx.behavior_kits[bk] });
      newBkList.push({
        behavior_kit_uuid: bk,
        name: companyEx.behavior_kits[bk],
        expanded: false,
      });
    });
    newBkItems.sort((a: DropdownItem, b: DropdownItem) => {
      return a.label > b.label ? 1 : -1;
    });
    newBkItems.unshift(bkItemsEmpty[0]);
    newBkList.sort((a: BehaviorKitMinimalInfo, b: BehaviorKitMinimalInfo) => {
      return a.name > b.name ? 1 : -1;
    });
    properties.forEach((property) => {
      values.set(property, companyEx[property as keyof typeof companyEx]);
    });
    const newLocals: CompanyUpdateInput = {
      companyUUID: companyEx?.external_id,
      updates: values,
    };
    setLocals(newLocals);
    setBkItems(newBkItems);
    setBkList(newBkList);
  }, [companyEx]);

  const navigateToEditBK = useCallback(
    (bk_uuid: string) => {
      const url = `/companies/${activeId}/behaviorkit/${bk_uuid}/edit`;
      navigate(url, {
        state:
          activeId === (subid ?? "") ? { parentCompanyUUID: companyId } : null,
      });
    },
    [navigate, activeId, subid, companyId]
  );

  useEffect(() => {
    if (bkCreate.isSuccess && bkCreate.data?.external_id) {
      navigateToEditBK(bkCreate.data?.external_id);
    }
  }, [bkCreate.data, bkCreate.isSuccess, navigateToEditBK]);

  function updateState(key: string, value: any) {
    let newLocals: CompanyUpdateInput = { ...locals };
    newLocals.updates.set(key, value);
    setLocals(newLocals);
    setDirty(true);
  }

  function setExpanded(key: string, value: boolean) {
    setBkList(
      bkList.map((bk: BehaviorKitMinimalInfo) =>
        bk.behavior_kit_uuid === key ? { ...bk, expanded: value } : bk
      )
    );
  }

  const { hide, modal, show } = useConfirmationDialog();
  useEffect(() => {
    transition?.retry();
  }, [transition]);
  useBlocker((tx: Transition) => {
    if (!dirty) {
      setTransition(tx);
      return;
    }
    show(
      "Leave without saving changes?",
      "You have unsaved changes. If you leave now, your edits will be lost.",
      (action: ConfirmDialogActions) => {
        if (action === "continue") {
          setDirty(false);
          setTransition(tx);
          return;
        } else {
          hide();
        }
      }
    );
  });

  return (
    <div className="p-7 bg-gray-50 max-w-[800px]">
      {modal}
      {loadingCompanyEx || !companyEx || !locals.updates.has(properties[0]) ? (
        <LoadingSpinner />
      ) : (
        <div>
          <div className="w-96 pb-9">
            <DropdownComboSimple
              name="default-bk"
              label="Default Behavior Kit"
              icon={
                <InformationCircleIcon height={20} width={20} color="grey" />
              }
              tooltip={
                "Behavior kits are the configurable filters that companies can use to set behavior flags look back. And keywords for any given check type."
              }
              items={bkItems}
              initial={bkItems.find(
                (opt) => opt.id === (companyEx.default_behavior_kit ?? "None")
              )}
              onChange={(value) => {
                updateState(
                  "default_behavior_kit",
                  value.id === "None" ? null : value.id
                );
              }}
              disabled={readOnly || loadingCompanyUpdate}
            />
          </div>
          <div className="flex">
            <div className="flex-0 pr-3">
              {loadingCompanyUpdate ? (
                <LoadingSpinner />
              ) : (
                <Button
                  text="Save Changes"
                  onClick={async () => {
                    await updateCompanyAsync(locals);
                    loadCompanyEx(activeId);
                    setDirty(false);
                  }}
                  disabled={readOnly}
                />
              )}
            </div>
            <div className="flex-1 p-1">
              <span className="pr-2">Last Updated by:</span>
              <span className="font-semibold">
                {companyEx.default_behavior_kit_assigned_by_name ??
                  companyEx.default_behavior_kit_assigned_by}
              </span>
            </div>
          </div>

          {companyUpdateError && (
            <div className="text-red-500 flex items-center gap-2 pt-2">
              <ExclamationTriangleIcon className="w-5 h-5" /> Error saving
              changes
            </div>
          )}
          <div className="text-center rounded py-9">
            <div className="font-bold text-lg w-full bg-gray-100 text-left p-3 rounded-t border border-gray-200">
              Behavior Kits
            </div>
            {(!bkList || bkList.length === 0) && (
              <div className="border border-gray-200 bg-white py-9 rounded-b">
                <div className="text-base text-gray-700 font-semibold pb-6">
                  No Behavior Kits Assigned
                </div>
                {bkCreate.isLoading && <LoadingSpinner />}
                {!readOnlyBehaviorKit && !bkCreate.isLoading && (
                  <Button
                    text={"Add Behavior Kit"}
                    type={ButtonTypes.add}
                    colorType={ButtonColorTypes.outline}
                    onClick={() => {
                      bkCreate.mutate({
                        company_uuid: activeId,
                        display_images: true,
                        name: `Behavior Kit - ${new Date().toISOString()}`,
                      });
                    }}
                  />
                )}
              </div>
            )}
            {bkList.length > 0 && (
              <>
                <div className="mb-4 bg-white">
                  {bkList.map((behaviorKit: BehaviorKitMinimalInfo) => (
                    <div
                      className="border-gray-200 border-x border-b p-3"
                      key={behaviorKit.behavior_kit_uuid}
                    >
                      <div className="flex gap-4 truncate w-full items-center justify-between">
                        <span className="font-bold cursor-pointer">
                          {behaviorKit.name}
                        </span>

                        <div className="flex gap-2">
                          {!readOnlyBehaviorKit && (
                            <div className="flex gap-2 ml-0 text-gray-500 justify-center items-center">
                              <PencilIcon
                                className="w-4 cursor-pointer"
                                onClick={() =>
                                  navigateToEditBK(
                                    behaviorKit.behavior_kit_uuid
                                  )
                                }
                              />
                              <CompanyBehaviorKitRemove
                                onRemove={() => {
                                  setBkList(
                                    bkList.filter(
                                      (bk) =>
                                        bk.behavior_kit_uuid !==
                                        behaviorKit.behavior_kit_uuid
                                    )
                                  );
                                }}
                                behaviorKitUUID={behaviorKit.behavior_kit_uuid}
                                companyUUID={activeId}
                              />
                            </div>
                          )}
                          <div className="text-gray-500">
                            {behaviorKit.expanded ? (
                              <ChevronUpIcon
                                className="w-5 h-5"
                                onClick={() =>
                                  setExpanded(
                                    behaviorKit.behavior_kit_uuid,
                                    false
                                  )
                                }
                              />
                            ) : (
                              <ChevronDownIcon
                                className="w-5 h-5"
                                onClick={() =>
                                  setExpanded(
                                    behaviorKit.behavior_kit_uuid,
                                    true
                                  )
                                }
                              />
                            )}
                          </div>
                        </div>
                      </div>
                      {behaviorKit.behavior_kit_uuid && (
                        <CompanyBehaviorKitDetail
                          behaviorKitUUID={behaviorKit.behavior_kit_uuid ?? ""}
                          isVisible={behaviorKit.expanded ?? false}
                        />
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex">
                  {bkCreate.isLoading && <LoadingSpinner />}
                  {!readOnlyBehaviorKit && !bkCreate.isLoading && (
                    <Button
                      text={"Add Behavior Kit"}
                      type={ButtonTypes.add}
                      colorType={ButtonColorTypes.outline}
                      onClick={() => {
                        bkCreate.mutate({
                          company_uuid: activeId,
                          display_images: true,
                          name: `Behavior Kit - ${new Date().toISOString()}`,
                        });
                      }}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BehaviorKits;
